/**
 * Natural sort key for a bus route code.
 *
 * Codes are zero-padded to two digits (`01`, `03A`, `04CT`), which makes plain
 * lexicographic order look right for the first ~20 entries and then break: it
 * yields `…09B, 09BCT, 100, 101A, 102…` where the three-digit codes sort into
 * the middle of the single-digit ones. Verified against the real dataset — the
 * divergence starts at index 21.
 *
 * Twelve codes have no numeric prefix at all (`BRT01`, `E01`–`E11`). The leading
 * group flag keeps them from colliding with numeric codes; without it a regex
 * miss would need a sentinel number and the tie-break would be unstable.
 *
 * `6969` is also present and looks like operator test data. It is deliberately
 * NOT filtered — the extraction pipeline is out of scope, and silently hiding a
 * route is worse than showing an odd one.
 */
export function routeSortKey(code: string): [number, number, string] {
  const match = /^(\d+)(.*)$/.exec(code)
  if (!match) return [1, 0, code]
  return [0, Number(match[1]), match[2] ?? '']
}

export function compareRouteCodes(a: string, b: string): number {
  const left = routeSortKey(a)
  const right = routeSortKey(b)
  if (left[0] !== right[0]) return left[0] - right[0]
  if (left[1] !== right[1]) return left[1] - right[1]
  return left[2].localeCompare(right[2])
}
